import { Fragment, type FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';

import { reportRecoveredRichEditorError } from '../../data-dog';
import { RichEditorToolbarWithTranscribe } from '../../job-architecture-v2';
import type { LearningOpportunityFormState } from '../form-state';

export const DescriptionSection: FunctionComponent<{
    readonly fallbackHtml?: string;
    readonly isEditing: boolean;
    readonly translationIndex: number;
}> = ({ fallbackHtml, isEditing, translationIndex }) => {
    const translate = useTranslate();
    const { control } = useFormContext<LearningOpportunityFormState>();
    const {
        field: { onChange, value },
    } = useController({ control, name: `translations.${translationIndex}.description` });

    const formValue = value ?? '';
    const [editorValue, setEditorValue] = useState(formValue);

    useEffect(() => {
        setEditorValue((current) => (current === formValue ? current : formValue));
    }, [formValue]);

    const handleChange = useCallback(
        (html: string) => {
            setEditorValue(html);
            onChange(html);
        },
        [onChange],
    );

    const handleEditorError = useCallback(
        (error: Error) => {
            reportRecoveredRichEditorError({ translationIndex }, error);
        },
        [translationIndex],
    );

    return (
        <Fragment>
            <h3 className="m-0 text-body-m font-medium text-text-primary">
                {translate('pages.learning-opportunity-details.tabs.course-description')}
            </h3>
            {isEditing ? (
                <div className="relative pt-[52px]">
                    <div className="overflow-hidden rounded-lg border border-border-muted">
                        <RichEditor
                            toolbar={<RichEditorToolbarWithTranscribe />}
                            value={editorValue}
                            onChange={handleChange}
                            onError={handleEditorError}
                        />
                    </div>
                </div>
            ) : (
                <div
                    className="prose prose-themed max-w-none text-text-form text-sm"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: Existing learning opportunity descriptions are stored as rich HTML.
                    dangerouslySetInnerHTML={{ __html: formValue || (fallbackHtml ?? '') }}
                />
            )}
        </Fragment>
    );
};
